import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { queryKeyChatId } from 'constants/url'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAssistantUrlChatSelection } from 'tripPlanner/assistant/hooks/useAssistantUrlChatSelection'
import { canBuildOfferPageSurfaceContext } from './buildOfferPageSurfaceContext'
import { OFFER_PAGE_ASSISTANT_MIN_SCREEN_SIZE } from './constants'
import { useOptionalOfferPageAssistantLayout } from './OfferPageAssistantLayoutContext'

const OFFER_PAGE_CHAT_ID_QUERY_PARAM_ALIASES = ['chat_id']
const loadOfferPageAssistantPanel = () => import('./OfferPageAssistantPanel')
const OfferPageAssistantPanel = lazy(loadOfferPageAssistantPanel)
const panelFallback = null

const DesktopOnly = styled(CSSBreakpoint)`
  height: 100%;
`

interface Props {
  offerPageState: OfferPageState
}

export default function OfferPageAssistantSurface({ offerPageState }: Props) {
  const {
    selectedChatId,
    urlChatId,
    selectChat,
    clearChatSelection,
    removeChatIdFromUrl,
  } = useAssistantUrlChatSelection({
    queryParam: queryKeyChatId,
    queryParamAliases: OFFER_PAGE_CHAT_ID_QUERY_PARAM_ALIASES,
  })
  const [isExpanded, setIsExpanded] = useState(selectedChatId !== undefined)
  const assistantLayout = useOptionalOfferPageAssistantLayout()
  const hasLayoutProvider = assistantLayout !== undefined
  const isLayoutEligible = assistantLayout?.isLayoutEligible ?? true
  const collapsedPortalElement = assistantLayout?.collapsedPortalElement
  const portalElement = assistantLayout?.portalElement
  const onAvailabilityChange = assistantLayout?.onAvailabilityChange ?? noop
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isBaseFeatureAvailable = isLayoutEligible &&
    isLoggedIn &&
    config.ENABLE_TRIP_PLANNER &&
    canBuildOfferPageSurfaceContext(offerPageState)
  const { isAnyVariant: isExperimentEnabled } = useExperiment(Experiments.tripHotelOfferSurface, {
    canActivate: isBaseFeatureAvailable,
  })
  const isFeatureAvailable = isBaseFeatureAvailable && isExperimentEnabled
  const handleExpand = useCallback(() => {
    if (selectedChatId !== undefined) {
      selectChat(selectedChatId)
    }

    setIsExpanded(true)
  }, [selectChat, selectedChatId])
  const handleCollapse = useCallback(() => {
    removeChatIdFromUrl()
    setIsExpanded(false)
  }, [removeChatIdFromUrl])
  const isCollapsedPortalPending = hasLayoutProvider && !isExpanded && collapsedPortalElement === undefined
  const isPortalPending = hasLayoutProvider && isExpanded && portalElement === undefined

  useEffect(() => {
    if (!hasLayoutProvider) {
      return
    }

    onAvailabilityChange(isFeatureAvailable && isExpanded)

    return () => onAvailabilityChange(false)
  }, [hasLayoutProvider, isExpanded, isFeatureAvailable, onAvailabilityChange])

  useEffect(() => {
    if (urlChatId !== undefined) {
      setIsExpanded(true)
    }
  }, [urlChatId])

  if (!isFeatureAvailable) {
    return null
  }

  return (
    <DesktopOnly min={OFFER_PAGE_ASSISTANT_MIN_SCREEN_SIZE} dynamic>
      <Suspense fallback={panelFallback}>
        <OfferPageAssistantPanel
          offerPageState={offerPageState}
          initialChatId={selectedChatId}
          collapsedPortalElement={collapsedPortalElement}
          isExpanded={isExpanded}
          isCollapsedPortalPending={isCollapsedPortalPending}
          isPortalPending={isPortalPending}
          portalElement={portalElement}
          onCollapse={handleCollapse}
          onExpand={handleExpand}
          onChatCleared={clearChatSelection}
          onChatSelected={selectChat}
        />
      </Suspense>
    </DesktopOnly>
  )
}
